/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { Editor } from 'cio/services/editor';
import { DEFAULT_CUSTOM_PAYLOAD_BODY_JSON } from 'cio/utils/push/custom-payload';
import { DEFAULT_RICH_PUSH_BODY_JSON } from 'cio/utils/push/rich-push';
import ComposerEditableEntity from 'cio/utils/composer/composer-editable-entity';
import CustomerModel from 'cio/models/customer';
import { PushPreview } from 'cio/utils/previews';
import PushService from 'cio/services/push';
import AnalyticsService from 'cio/services/analytics';
import { tracked } from '@glimmer/tracking';
import NewsletterModel from 'cio/models/newsletter';
import ActionModel from 'cio/models/action';
import TemplateModel from 'cio/models/template';
import { SwitchModalType } from 'cio/components/action-composer/types/push/switch-modal';
import TransactionalMessageModel from 'cio/models/transactional-message';

interface PushContainerArgs {
  editableEntity: ComposerEditableEntity;
  preview: PushPreview;
  errors: string[];
  isDirty: boolean;
  isPreviewLoading: boolean;
  selectedCustomer?: CustomerModel;
  hasReadOnlyPermission: boolean;
}

export default class PushContainer extends Component<PushContainerArgs> {
  @service push!: PushService;
  @service analytics!: AnalyticsService;

  @tracked currentTab?: string;
  @tracked currentSwitchModal?: SwitchModalType;

  constructor(owner: unknown, args: PushContainerArgs) {
    super(owner, args);

    let model = this.getModel();

    if (model.isPlatformSelected) {
      this.currentTab = model.sendToPlatform;
    } else {
      this.currentTab = this.enabledPlatforms.firstObject?.key;
    }
  }

  get isCustomPayloadEditor() {
    return this.template.editor === Editor.JSON;
  }

  get template() {
    return this.args.editableEntity.template as TemplateModel;
  }

  get enabledPlatforms() {
    return this.push.platforms;
  }

  @action
  switchEditor(editor: Editor) {
    this.resetBodyJson(editor);
    this.args.editableEntity.template?.set('editor', editor);
    this.args.editableEntity.template?.set('body', '');
    this.args.editableEntity.template?.set('imageUrl', '');
    this.args.editableEntity.template?.set('subject', '');
    this.currentSwitchModal = SwitchModalType.none;
  }

  resetBodyJson(editor: Editor) {
    if (editor === Editor.JSON) {
      this.args.editableEntity.template?.set('bodyJson', DEFAULT_CUSTOM_PAYLOAD_BODY_JSON);
    } else if (editor === Editor.RICH) {
      this.args.editableEntity.template?.set('bodyJson', DEFAULT_RICH_PUSH_BODY_JSON);
    }
  }

  get payloadSnippet() {
    try {
      let payload = JSON.parse(this.template.bodyJson);
      payload.ios = JSON.parse(payload.ios);
      payload.android = JSON.parse(payload.android);
      return JSON.stringify(payload, null, 2);
    } catch (e) {
      return this.template.bodyJson;
    }
  }

  @action
  showSwitchEditorModal(to: Editor) {
    let currentEditor = this.template.editor;
    if (to === currentEditor) {
      return;
    }

    if (to === Editor.JSON) {
      this.currentSwitchModal = SwitchModalType.richToCustom;
    } else if (to === Editor.RICH) {
      this.currentSwitchModal = SwitchModalType.customToRich;
    }
  }

  @action
  closeSwitchEditorModal(...args: Editor[]) {
    let [to] = args;
    // args can be PointerEvents if editor is not passed explicitly
    if ([Editor.RICH, Editor.JSON].includes(to)) {
      this.switchEditor(args[0]);
    }
    this.currentSwitchModal = SwitchModalType.none;
  }

  private getModel() {
    if (this.args.editableEntity.isTransactionalMessage) {
      return this.args.editableEntity.message as TransactionalMessageModel;
    } else if (this.args.editableEntity.isNewsletter) {
      return this.args.editableEntity.newsletter as NewsletterModel;
    } else {
      return this.args.editableEntity.action as ActionModel;
    }
  }
}
